/* eslint-disable no-unused-vars */
// #region Dependencias
import {
  collection,
  deleteDoc,
  doc,
  getDocs,
  limit,
  query,
  setDoc,
  where
} from 'firebase/firestore'
import { connectFunctionsEmulator, httpsCallable } from 'firebase/functions'
import { deleteObject, getDownloadURL, ref, uploadBytes } from 'firebase/storage'
import { db, functions, storage } from 'services/firebase'
import i18n from 'services/i18n'
import {
  getPages,
  getPageByURL,
  deletePage,
  updatePage,
  createPage
} from 'store/slices/pagesSlice'
import { finishLoading, setDialogoAbierto, startLoading } from 'store/slices/uiSlice'
import { responseMessageHandler } from 'utils/responseMessageHandler'
// #endregion

/**
 * Thunk para obtener todas las paginas activas registradas en Firestore y
 * establecerlas en el estado mediante la acción {@link getPages}.
 *
 * @return {Function} Función que envía las acciones asíncronas al store de Redux.
 */
export const getPagesRequest = () => async (dispatch) => {
  dispatch(startLoading())
  const q = query(collection(db, 'paginas'), where('estado', '==', true))
  getDocs(q)
    .then((documents) => {
      const paginas = []
      documents.forEach((doc) => paginas.push(doc.data()))
      dispatch(getPages(paginas))
    })
    .catch((err) => responseMessageHandler(err, dispatch))
    .finally(() => dispatch(finishLoading()))
}

/**
 * Thunk para obtener los datos de una pagina por medio de su URL y establecerlos
 * en el estado mediante la acción {@link getPageByURL}.
 *
 * @param {string} url - URL única de la página.
 * @param {Function} navigate - Función que permite navegar a otra ruta.
 *
 * @return {Function} Función que envía las acciones asíncronas al store de Redux.
 */
export const getPagesByURLRequest = (url, navigate) => async (dispatch) => {
  dispatch(startLoading())

  const q = query(collection(db, 'paginas'), where('url', '==', url), limit(1))
  let pagina
  getDocs(q)
    .then((documents) => {
      if (documents.docs.length === 0) return navigate('/404')
      pagina = documents.docs[0].data()
      dispatch(getPageByURL(pagina))
    })
    .finally(() => dispatch(finishLoading()))
}

/**
 * Función que sube un archivo a Firebase Storage y retorna la URL de descarga del archivo subido.
 *
 * @param {string} id - ID de la pagina que esta vinculada al archivo.
 * @param {string} archivo - Archivo en formato blob.
 * @param {string} tipo - Tipo de archivo (secciones.hero.imagen o secciones.main.imagen).
 * @return {Promise<string>} URL de descarga del archivo subido.
 */
const uploadFileFirebase = async (id, archivo, tipo) => {
  if (!archivo?.startsWith('blob:')) return Promise.resolve(archivo)

  const archivosRef = ref(storage, `${id}/${tipo}`)
  return fetch(archivo)
    .then((res) => res.blob())
    .then((blob) => uploadBytes(archivosRef, blob))
    .then((snapshot) => getDownloadURL(snapshot.ref))
}

/**
 * Función que elimina un archivo de Firebase Storage.
 *
 * @param {string} id - ID de la pagina que esta vinculada al archivo.
 * @param {string} archivo - Referencia del archivo de Firebase Storage.
 * @param {string} tipo - Tipo de archivo (secciones.hero.imagen o secciones.main.imagen).
 * @return {Promise<void>} Promesa de eliminación del archivo
 */
const deleteFileFirebase = async (id, archivo, tipo) => {
  if (!archivo) return
  const archivosRef = ref(storage, `${id}/${tipo}`)
  return deleteObject(archivosRef)
}

/**
 * Thunk que crea una nueva página en Firestore y sube las imágenes correspondientes a las
 * secciones mediante la función {@link uploadFileFirebase} para posteriormente
 * redirigir al usuario hacia la ruta de la página creada y añadirla al estado
 * mediante la acción {@link createPage}.
 *
 * @param {Object} pagina - Objeto con los datos de la nueva página a crear.
 * @param {Function} navigate - Función que permite navegar a otra ruta.
 *
 * @return {Function} Función que envía las acciones asíncronas al store de Redux.
 */
export const createPageRequest = (pagina, navigate) => async (dispatch) => {
  dispatch(startLoading())
  const paginaRef = doc(collection(db, 'paginas'))

  let paginaNueva = {}

  Promise.all([
    uploadFileFirebase(paginaRef.id, pagina.secciones.hero.ES.imagen, 'secciones.hero.ES.imagen'),
    uploadFileFirebase(paginaRef.id, pagina.secciones.hero.EN.imagen, 'secciones.hero.EN.imagen'),
    uploadFileFirebase(paginaRef.id, pagina.secciones.main.ES.imagen, 'secciones.main.ES.imagen'),
    uploadFileFirebase(paginaRef.id, pagina.secciones.main.EN.imagen, 'secciones.main.EN.imagen'),
    uploadFileFirebase(paginaRef.id, pagina.secciones.subhero.imagen, 'secciones.subhero.imagen'),
    // LOGOS
    uploadFileFirebase(
      paginaRef.id,
      pagina.secciones.timeline.items[0].logo,
      'secciones.timeline.0.logo'
    ),
    uploadFileFirebase(
      paginaRef.id,
      pagina.secciones.timeline.items[1].logo,
      'secciones.timeline.1.logo'
    ),
    uploadFileFirebase(
      paginaRef.id,
      pagina.secciones.timeline.items[2].logo,
      'secciones.timeline.2.logo'
    )
  ])
    .then(([heroEsUrl, heroEnUrl, mainEsUrl, mainEnUrl, subheroUrl, tlLogo0, tlLogo1, tlLogo2]) => {
      paginaNueva = {
        id: paginaRef.id,
        ...pagina,
        secciones: {
          ...pagina.secciones,
          hero: {
            ...pagina.secciones.hero,
            ES: {
              ...pagina.secciones.hero.ES,
              imagen: heroEsUrl
            },
            EN: {
              ...pagina.secciones.hero.EN,
              imagen: heroEnUrl
            }
          },
          main: {
            ...pagina.secciones.main,
            ES: {
              ...pagina.secciones.main.ES,
              imagen: mainEsUrl
            },
            EN: {
              ...pagina.secciones.main.EN,
              imagen: mainEnUrl
            }
          },
          subhero: {
            ...pagina.secciones.subhero,
            imagen: subheroUrl
          },
          timeline: {
            ...pagina.secciones.timeline,
            items: [
              {
                ...pagina.secciones.timeline.items[0],
                logo: tlLogo0
              },
              {
                ...pagina.secciones.timeline.items[1],
                logo: tlLogo1
              },
              {
                ...pagina.secciones.timeline.items[2],
                logo: tlLogo2
              }
            ]
          }
        },
        estado: true,
        fechaCreacion: Date.now(),
        fechaActualizacion: Date.now()
      }
      return setDoc(paginaRef, paginaNueva)
    })
    .then(() => {
      dispatch(
        setDialogoAbierto({
          abierto: true,
          tipo: 'success',
          titulo: 'Creación exitosa',
          mensaje: 'La página se ha creado correctamente'
        })
      )
      dispatch(createPage(paginaNueva))
      navigate(`/pages/${pagina.url}/editor`)
    })
    .catch((err) => responseMessageHandler(err, dispatch))
    .finally(() => dispatch(finishLoading()))
}

/**
 * Thunk que edita los datos de una página en Firestore, sobre-escribe las imágenes
 * correspondientes a las secciones mediante la función {@link uploadFileFirebase}
 * y posteriormente la modifica en el estado mediante la acción {@link updatePage}.
 *
 * @param {Object} pagina - Objeto con los datos de la página a editar.
 *
 * @return {Function} Función que envía las acciones asíncronas al store de Redux.
 */
export const updatePageRequest = (pagina) => async (dispatch) => {
  dispatch(startLoading())

  const paginaRef = doc(collection(db, 'paginas'), pagina.id)
  let paginaNueva = {}
  Promise.all([
    uploadFileFirebase(paginaRef.id, pagina.secciones.hero.ES.imagen, 'secciones.hero.ES.imagen'),
    uploadFileFirebase(paginaRef.id, pagina.secciones.hero.EN.imagen, 'secciones.hero.EN.imagen'),
    uploadFileFirebase(paginaRef.id, pagina.secciones.main.ES.imagen, 'secciones.main.ES.imagen'),
    uploadFileFirebase(paginaRef.id, pagina.secciones.main.EN.imagen, 'secciones.main.EN.imagen'),
    uploadFileFirebase(paginaRef.id, pagina.secciones.subhero.imagen, 'secciones.subhero.imagen'),
    // LOGOS
    uploadFileFirebase(
      paginaRef.id,
      pagina.secciones.timeline.items[0].logo,
      'secciones.timeline.0.logo'
    ),
    uploadFileFirebase(
      paginaRef.id,
      pagina.secciones.timeline.items[1].logo,
      'secciones.timeline.1.logo'
    ),
    uploadFileFirebase(
      paginaRef.id,
      pagina.secciones.timeline.items[2].logo,
      'secciones.timeline.2.logo'
    )
  ])
    .then(([heroEsUrl, heroEnUrl, mainEsUrl, mainEnUrl, subheroUrl, tlLogo0, tlLogo1, tlLogo2]) => {
      paginaNueva = {
        ...pagina,
        secciones: {
          ...pagina.secciones,
          hero: {
            ...pagina.secciones.hero,
            ES: {
              ...pagina.secciones.hero.ES,
              imagen: heroEsUrl
            },
            EN: {
              ...pagina.secciones.hero.EN,
              imagen: heroEnUrl
            }
          },
          main: {
            ...pagina.secciones.main,
            ES: {
              ...pagina.secciones.main.ES,
              imagen: mainEsUrl
            },
            EN: {
              ...pagina.secciones.main.EN,
              imagen: mainEnUrl
            }
          },
          subhero: {
            ...pagina.secciones.subhero,
            imagen: subheroUrl
          },
          timeline: {
            ...pagina.secciones.timeline,
            items: [
              {
                ...pagina.secciones.timeline.items[0],
                logo: tlLogo0
              },
              {
                ...pagina.secciones.timeline.items[1],
                logo: tlLogo1
              },
              {
                ...pagina.secciones.timeline.items[2],
                logo: tlLogo2
              }
            ]
          }
        },
        fechaActualizacion: Date.now()
      }
      return setDoc(paginaRef, paginaNueva)
    })
    .then(() => {
      dispatch(
        setDialogoAbierto({
          abierto: true,
          tipo: 'success',
          titulo: 'Actualización exitosa',
          mensaje: 'La página se ha actualizado correctamente'
        })
      )
      dispatch(updatePage(paginaNueva))
    })
    .catch((err) => responseMessageHandler(err, dispatch))
    .finally(() => dispatch(finishLoading()))
}

/**
 * Thunk que elimina una pagina de Firestore junto con sus respectivas imágenes
 * en Firebase Storage mediante la función {@link deleteFileFirebase}
 * y posteriormente la elimina del estado mediante la acción {@link deletePage}.
 *
 * @param {Object} pagina - Objeto de la página a eliminar.
 * @return {Function} Función que envía las acciones asíncronas al store de Redux.
 */
export const deletePageRequest = (pagina) => async (dispatch) => {
  dispatch(startLoading())
  const paginaRef = doc(db, `paginas/${pagina.id}`)

  Promise.all([
    deleteFileFirebase(pagina.id, pagina.secciones.hero.ES.imagen, 'secciones.hero.ES.imagen'),
    deleteFileFirebase(pagina.id, pagina.secciones.hero.EN.imagen, 'secciones.hero.EN.imagen'),
    deleteFileFirebase(pagina.id, pagina.secciones.main.ES.imagen, 'secciones.main.ES.imagen'),
    deleteFileFirebase(pagina.id, pagina.secciones.main.EN.imagen, 'secciones.main.EN.imagen'),
    deleteFileFirebase(pagina.id, pagina.secciones.subhero.imagen, 'secciones.subhero.imagen'),
    // LOGOS
    deleteFileFirebase(
      pagina.id,
      pagina.secciones.timeline.items[0].logo,
      'secciones.timeline.0.logo'
    ),
    deleteFileFirebase(
      pagina.id,
      pagina.secciones.timeline.items[1].logo,
      'secciones.timeline.1.logo'
    ),
    deleteFileFirebase(
      pagina.id,
      pagina.secciones.timeline.items[2].logo,
      'secciones.timeline.2.logo'
    )
  ])
    .then(() => {
      deleteDoc(paginaRef)
      dispatch(
        setDialogoAbierto({
          abierto: true,
          tipo: 'success',
          titulo: 'Eliminación exitosa',
          mensaje: 'La página se ha eliminado correctamente'
        })
      )
      dispatch(deletePage(pagina))
    })
    .catch((err) => responseMessageHandler(err, dispatch))
    .finally(() => dispatch(finishLoading()))
}

/**
 * Thunk que realiza una solicitud de pago para el producto seleccionado
 * a Firebase Functions {@link makePayment}
 *
 * @param {string} Correo - Correo electrónico para realizar el pago.
 * @return {Function} Función que envía las acciones asíncronas al store de Redux.
 */

export const makePaymentRequest = (datosPago) => async (dispatch, getState) => {
  dispatch(startLoading())

  const { paginaSeleccionada } = getState().pages

  // connectFunctionsEmulator(functions, "localhost", 5001); // dev
  const makePayment = httpsCallable(functions, 'makePayment')

  const datos = {
    paginaId: paginaSeleccionada.id,
    correo: datosPago.correo,
    telefono: datosPago.telefono,
    callbackUrl: window.location.href,
    idioma: i18n.language
  }
  makePayment({ datos })
    .then(({ data }) => {
      if (+data.status_code === 100 && data.redirect_url) window.location.assign(data.redirect_url)
    })
    .catch((err) => responseMessageHandler(err, dispatch))
    .finally(() => dispatch(finishLoading()))
}

export const checkPaymentRequest = (orden, setSearchParams) => async (dispatch, getState) => {
  dispatch(startLoading())

  const { paginaSeleccionada } = getState().pages

  // connectFunctionsEmulator(functions, "localhost", 5001); // dev
  const checkPayment = httpsCallable(functions, 'checkPayment')

  const datos = {
    ordenId: orden.id,
    paginaId: paginaSeleccionada.id,
    correo: orden.correo,
    telefono: orden.telefono,
    idioma: i18n.language,
    url: window.location.href
  }

  checkPayment({ datos })
    .then(({ data }) => {
      const tipoDialogo =
        +data.code >= 100 && +data.code <= 199
          ? 'info'
          : (+data.code >= 200 && +data.code <= 299) || (+data.code >= 600 && +data.code <= 799)
              ? 'info'
              : 'error'

      dispatch(
        setDialogoAbierto({
          abierto: true,
          tipo: tipoDialogo,
          titulo: `${i18n.t('payments.' + data?.status.toLowerCase())}: #${orden.id}`,
          mensaje:
            tipoDialogo === 'error'
              ? i18n.t('payments.' + data?.code) + ' ' + i18n.t('payments.contact-bank')
              : i18n.t('payments.' + data?.code),
          callback: () => setSearchParams({})
        })
      )
    })
    .catch((err) => responseMessageHandler(err, dispatch))
    .finally(() => dispatch(finishLoading()))
}
